import Flex from "antd/lib/flex";
import AntdImage from "antd/lib/image";
import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Transforms, Text, Editor } from "slate";
import { ReactEditor } from "slate-react";

import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";

import { RendererProps } from "./types";
import { isValidUrl } from "./utils";
import { CodeMirrorBase } from "../codemirror/CodeMirrorBase";

const HeadingOne = forwardRef<HTMLHeadingElement, RendererProps>(function HeadingOne({ children, ...props }, ref) {
    return (
        <h3 {...props} className="text-center text-color-ant-primary!" ref={ref}>
            {children}
        </h3>
    );
});

const HeadingTwo = forwardRef<HTMLHeadingElement, RendererProps>(function HeadingTwo({ children, ...props }, ref) {
    return (
        <h4 {...props} ref={ref}>
            {children}
        </h4>
    );
});

const HeadingThree = forwardRef<HTMLHeadingElement, RendererProps>(function HeadingThree({ children, ...props }, ref) {
    return (
        <h5 {...props} ref={ref}>
            <Flex justify="flex-start" align="center" className="mt-4">
                <ArrowRight className="mx-3 text-red-base!" fill="none" size={12} />
                {children}
            </Flex>
        </h5>
    );
});

const Paragraph = forwardRef<HTMLParagraphElement, RendererProps>(function Paragraph({ children, ...props }, ref) {
    return (
        <p ref={ref} {...props}>
            {children}
        </p>
    );
});

const Citation = forwardRef<HTMLQuoteElement, RendererProps>(function Citation({ children, ...props }, ref) {
    return (
        <blockquote
            {...props}
            className="border-color-ant-primary mt-0! ml-2! border-l-3 pl-4! text-color-ant-secondary italic [&:has(+blockquote)]:mb-0!"
            ref={ref}
        >
            {children}
        </blockquote>
    );
});

const Image = forwardRef<HTMLImageElement, RendererProps>(function Image({
    src = "",
    alt = "",
    isReadOnly = false,
    element,
    editor,
    ...props
}) {
    const [t] = useTranslation();

    const handleImageClick = () => {
        if (element.type === "image") {
            const newUrl = prompt(t("inputs.RichText.modifyImageUrl"), element.url);
            if (newUrl && newUrl !== element.url) {
                if (!isValidUrl(newUrl)) {
                    alert(t("inputs.RichText.invalidUrl"));
                    return;
                }
                try {
                    const path = ReactEditor.findPath(editor, element);
                    Transforms.setNodes(editor, { url: newUrl }, { at: path });
                } catch {
                    alert(t("inputs.RichText.imageError"));
                }
            }
        }
    };

    return (
        <Flex justify="center" className="my-4">
            <AntdImage
                width={200}
                src={src}
                alt={alt}
                preview={isReadOnly}
                onClick={isReadOnly ? undefined : handleImageClick}
                {...props}
            />
        </Flex>
    );
});

const Link = forwardRef<HTMLAnchorElement, RendererProps>(function Link(
    { href, text, children, isReadOnly = true, editor, ...props },
    ref,
) {
    const [t] = useTranslation();

    const handleLinkClick = (event: React.MouseEvent) => {
        event.preventDefault();
        const newUrl = prompt(t("inputs.RichText.modifyLinkUrl"), href);
        if (newUrl && newUrl !== href) {
            if (!isValidUrl(newUrl)) {
                alert(t("inputs.RichText.invalidUrl"));
                return;
            }

            ReactEditor.focus(editor);

            const entries = Array.from(
                Editor.nodes(editor, {
                    match: n => Text.isText(n) && n.link === href && n.text === text,
                    universal: true,
                }),
            );

            for (const [node, path] of entries) {
                if (Text.isText(node)) {
                    Transforms.setNodes(editor, { ...node, link: newUrl }, { at: path });
                }
            }
        }
    };

    return (
        <a
            {...props}
            ref={ref}
            href={href}
            className="cursor-pointer underline"
            onClick={isReadOnly ? undefined : handleLinkClick}
            title={t("inputs.RichText.clickToModifyUrl")}
        >
            {children}
        </a>
    );
});

const CodeBlock = ({ children, language = "text" }: RendererProps) => {
    return <CodeMirrorBase value={String(children)} language={language} />;
};

export { HeadingOne, HeadingTwo, HeadingThree, Paragraph, Citation, Image, CodeBlock, Link };
